import React, { useState, useRef } from "react";
import { colors, fonts } from "../theme";
import { categories, contact } from "../data";
import { Eyebrow, PhoneLink, AddressLink } from "../ui";
import { Field, TextField, TextArea } from "../form";
import Select from "../Select";
import { validateContactForm, CONTACT_FIELD_ORDER } from "../validation";
import { openWhatsApp } from "../whatsapp";
import {
  canSubmitDirectly,
  contactFields,
  contactMessage,
  submitEnquiry,
} from "../enquiry";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import { webPageSchema, breadcrumbSchema, placeSchema } from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Contact", path: "/contact" },
];

/* The field label and input styles used to be declared again here, privately.
   Two copies of the same thing drift, and these had: the local input was still
   14px, which is exactly what makes Safari on iOS zoom the viewport in on
   focus, and the local label was muted2 (3.83:1). Both now come from ui.jsx,
   where the fix lives once. */

const infoLabel = {
  fontFamily: fonts.mono,
  fontSize: "var(--fs-label)",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: colors.brown,
  marginBottom: "8px",
};

const EMPTY = { name: "", company: "", city: "", contact: "", product: "", requirement: "" };

// The category list plus the escape hatch, in the order the dropdown shows
// them. Built once at module scope rather than inside the render: `categories`
// never changes, and a fresh array on every keystroke would be handing Select
// a new `options` identity for no reason.
const PRODUCT_OPTIONS = [
  ...categories.map((cat) => ({ value: cat.name, label: cat.name })),
  { value: "Other / not sure", label: "Other / not sure" },
];

// Shared by both submit routes so they read as equal weight. minHeight holds
// the 44px touch floor independently of the padding token.
const btnStyle = {
  minHeight: "46px",
  padding: "var(--btn-py) var(--btn-px)",
  borderRadius: "2px",
  fontSize: "var(--btn-fs)",
  fontWeight: 500,
  cursor: "pointer",
};

export default function Contact() {
  // idle → sending → sent (delivered to us) | whatsapp (handed to WhatsApp).
  // The two endings get different confirmations because they promise different
  // things: one says we have it, the other says the visitor still has to press
  // send in another app.
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  // Which fields have been blurred at least once — or, after a failed submit
  // attempt, all of them at once (see focusFirstInvalid). An error only ever
  // shows for a touched field, so the visitor isn't told the form is wrong
  // before they've had a chance to fill it in. errors itself is derived on
  // every render rather than stored, so a field's message clears the moment
  // a keystroke fixes it — no separate re-validate-on-change step needed.
  const [touched, setTouched] = useState({});
  const fieldRefs = useRef({});

  const setField = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setRef = (key) => (el) => { fieldRefs.current[key] = el; };
  const onBlurField = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const errors = validateContactForm(form);
  const shownError = (key) => (touched[key] ? errors[key] : undefined);

  // Shared by both submit routes: marks every field touched so all errors
  // surface together, then moves focus to the first invalid control in
  // declared field order rather than an arbitrary one.
  const focusFirstInvalid = (formErrors) => {
    setTouched(Object.fromEntries(CONTACT_FIELD_ORDER.map((k) => [k, true])));
    const firstInvalid = CONTACT_FIELD_ORDER.find((k) => formErrors[k]);
    if (firstInvalid) fieldRefs.current[firstInvalid]?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(errors);
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await submitEnquiry({
        kind: "Contact form",
        subject: `Website enquiry — ${form.name.trim() || "no name given"}`,
        replyTo: form.contact,
        pairs: contactFields(form),
      });
      setStatus("sent");
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  };

  // Deliberately not the form's submit handler: WhatsApp is a parallel route to
  // us, not a fallback for a failed send, so it validates the same fields —
  // through the same validator the submit path uses, now that native
  // `required` and reportValidity() are gone — and then leaves the form
  // untouched in case the visitor comes back to it.
  const onWhatsApp = () => {
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(errors);
      return;
    }
    openWhatsApp(contactMessage(form));
    setStatus("whatsapp");
  };

  const onReset = () => {
    setForm(EMPTY);
    setTouched({});
    setError("");
    setStatus("idle");
  };

  return (
    <main
      className="se-page"
      style={{
        ...wrap,
        padding: "clamp(36px,8vw,60px) var(--sp-gutter) clamp(48px,11vw,84px)",
      }}
    >
      <Seo
        title={PAGE_META.contact.title}
        description={PAGE_META.contact.description}
        path="/contact"
        schema={[
          webPageSchema({
            path: "/contact",
            name: PAGE_META.contact.title,
            description: PAGE_META.contact.description,
            type: "ContactPage",
            breadcrumbId: "https://sigmapapermart.com/contact#breadcrumb",
          }),
          breadcrumbSchema(CRUMBS, "/contact"),
          placeSchema(),
        ]}
      />

      <Eyebrow>Contact &amp; Enquiry</Eyebrow>
      <h1 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h1)", fontWeight: 500, margin: "12px 0 0", maxWidth: "20ch" }}>
        Tell us what you need
      </h1>

      <div
        className="se-contact2"
        style={{ display: "grid", gridTemplateColumns: "0.85fr 1.15fr", gap: "56px", marginTop: "38px", alignItems: "start" }}
      >
        {/* Contact info */}
        <div>
          <div style={{ marginBottom: "28px" }}>
            <div style={infoLabel}>Phone</div>
            {/* The single most-tapped thing on this page, and it was a bare
                26px-tall text run. inline-flex + a 44px floor blocks it out to
                a real target; --fs-lead tops out at the 19px it already was, so
                nothing changes on desktop. */}
            <PhoneLink
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "44px",
                padding: "6px 0",
                fontSize: "var(--fs-lead)",
                fontWeight: 600,
              }}
            />
          </div>
          <div style={{ marginBottom: "28px" }}>
            <div style={infoLabel}>Address</div>
            <AddressLink style={{ display: "block", fontSize: "var(--fs-body)", lineHeight: 1.6, color: colors.ink3, cursor: "pointer" }}>
              B-33/13, Paper Market,<br />Ramesh Nagar, Raja Garden,<br />New Delhi — 110015
            </AddressLink>
          </div>
          <div style={{ marginBottom: "28px" }}>
            <div style={infoLabel}>Hours</div>
            <div style={{ fontSize: "var(--fs-body)", lineHeight: 1.6, color: colors.ink3 }}>{contact.hours}</div>
          </div>
          <div
            style={{
              aspectRatio: "3/2",
              border: `1px solid ${colors.border}`,
              borderRadius: "4px",
              overflow: "hidden",
              marginTop: "8px",
            }}
          >
            <iframe
              title="Sigma Enterprises location"
              src={contact.mapEmbed}
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href={contact.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              // Was a ~20px-tall run under the map. inline-flex with a 44px
              // floor keeps the underline where it is and the target thumb-sized.
              display: "inline-flex",
              alignItems: "center",
              minHeight: "44px",
              marginTop: "10px",
              fontSize: "var(--fs-sm)",
              color: colors.brown,
              borderBottom: "1px solid #C9B896",
              paddingBottom: "2px",
            }}
          >
            Get directions →
          </a>
        </div>

        {/* Form / confirmation */}
        {/* .se-panel pulls the 34px side padding back to 16-24px below 560px.
            At 320px the desktop value alone was 68px — 39% of the screen — on
            top of the page gutters. */}
        <div className="se-panel" style={{ border: `1px solid ${colors.border}`, borderRadius: "5px", background: colors.white, padding: "34px 34px 38px" }}>
          {status === "idle" || status === "sending" ? (
            <form onSubmit={onSubmit} noValidate>
              {/* auto-fit + minmax(min(200px,100%),1fr) does the single-column
                  collapse from the container's own width, so it works inside
                  this card rather than at a viewport breakpoint. The min()
                  keeps the 200px floor from exceeding a narrower column and
                  pushing the page sideways. */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(200px,100%),1fr))", gap: "18px" }}>
                <TextField
                  ref={setRef("name")}
                  id="contact-name"
                  label="Name"
                  error={shownError("name")}
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  value={form.name}
                  onChange={setField("name")}
                  onBlur={onBlurField("name")}
                />
                <TextField
                  ref={setRef("company")}
                  id="contact-company"
                  label="Company"
                  error={shownError("company")}
                  type="text"
                  autoComplete="organization"
                  placeholder="Company name"
                  value={form.company}
                  onChange={setField("company")}
                  onBlur={onBlurField("company")}
                />
                <TextField
                  ref={setRef("city")}
                  id="contact-city"
                  label="Delivery city"
                  error={shownError("city")}
                  type="text"
                  autoComplete="address-level2"
                  placeholder="e.g. New Delhi"
                  value={form.city}
                  onChange={setField("city")}
                  onBlur={onBlurField("city")}
                />
                <TextField
                  ref={setRef("contact")}
                  id="contact-contact"
                  label="Phone / Email"
                  error={shownError("contact")}
                  type="text"
                  // Deliberately no autoComplete/inputMode: this one field takes
                  // either a number or an address, so any hint we gave would be
                  // wrong half the time — a tel keypad on an email, or an
                  // email autofill over a phone number.
                  placeholder="How to reach you"
                  value={form.contact}
                  onChange={setField("contact")}
                  onBlur={onBlurField("contact")}
                />
                {/* Field wires the label to the control by htmlFor/id rather
                    than wrapping it, which is what lets a listbox built from
                    button + ul sit here at all — see the note in form.jsx.
                    Select takes the label's id for aria-labelledby so it
                    announces "Product of interest, Art Card" the way the
                    native <select> it replaces did. */}
                <Field id="contact-product" label="Product of interest" error={shownError("product")}>
                  <Select
                    id="contact-product"
                    labelId="contact-product-label"
                    options={PRODUCT_OPTIONS}
                    placeholder="Select a category"
                    value={form.product}
                    onChange={setField("product")}
                    onBlur={onBlurField("product")}
                    invalid={Boolean(shownError("product"))}
                    describedBy={shownError("product") ? "contact-product-error" : undefined}
                  />
                </Field>
              </div>
              <TextArea
                ref={setRef("requirement")}
                id="contact-requirement"
                label="Requirement"
                error={shownError("requirement")}
                style={{ marginTop: "18px" }}
                rows="4"
                placeholder="Grade, grammage, size, quantity…"
                value={form.requirement}
                onChange={setField("requirement")}
                onBlur={onBlurField("requirement")}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "22px" }}>
                {/* Only offered when a build actually has an enquiry backend
                    configured — otherwise WhatsApp carries the form alone, as
                    it did before, rather than the page showing a button that
                    can only fail. */}
                {canSubmitDirectly && (
                  <button
                    type="submit"
                    className="se-btn-dark"
                    disabled={status === "sending"}
                    style={{
                      ...btnStyle,
                      opacity: status === "sending" ? 0.65 : 1,
                      cursor: status === "sending" ? "progress" : "pointer",
                    }}
                  >
                    {status === "sending" ? "Sending…" : "Send enquiry →"}
                  </button>
                )}
                <button
                  type={canSubmitDirectly ? "button" : "submit"}
                  onClick={canSubmitDirectly ? onWhatsApp : undefined}
                  className={canSubmitDirectly ? "se-btn-ghost" : "se-btn-dark"}
                  disabled={status === "sending"}
                  style={btnStyle}
                >
                  Send on WhatsApp
                </button>
              </div>

              {error && (
                <p
                  role="alert"
                  style={{
                    fontSize: "var(--fs-meta)",
                    lineHeight: 1.55,
                    color: colors.danger,
                    background: colors.dangerBg,
                    border: `1px solid ${colors.dangerBorder}`,
                    borderRadius: "3px",
                    padding: "10px 12px",
                    margin: "14px 0 0",
                  }}
                >
                  {error} You can send it on WhatsApp instead, or message us on{" "}
                  <PhoneLink style={{ color: colors.danger, fontWeight: 600 }} />.
                </p>
              )}

              {/* This is the only thing that tells the reader where each button
                  sends them, and it was 12.5px in muted3 (#A79E8D) — 2.65:1 on
                  white, well under the 4.5:1 floor. muted is 5.57:1. */}
              <p style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, color: colors.muted, margin: "16px 0 0" }}>
                {canSubmitDirectly
                  ? "Send it straight to us, or open WhatsApp with the same details prefilled. Either way we typically respond within one working day."
                  : "Opens WhatsApp with your enquiry ready to send. We typically respond within one working day."}
              </p>
            </form>
          ) : (
            /* Submitting swaps the whole form out for this panel without moving
               focus, so a screen reader was left on a button that no longer
               exists and heard nothing announced. role=status + aria-live=polite
               announces the swap without stealing focus mid-gesture. */
            <div role="status" aria-live="polite" style={{ padding: "30px 4px", textAlign: "center" }}>
              <div
                aria-hidden="true"
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  background: "#EAF1E4",
                  border: "1px solid #CBDDBB",
                  color: "#4a7a34",
                  display: "grid",
                  placeItems: "center",
                  fontSize: "24px",
                  margin: "0 auto 18px",
                }}
              >
                ✓
              </div>
              <h3 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h3)", fontWeight: 500, margin: "0 0 8px" }}>
                {status === "sent" ? "Enquiry received" : "WhatsApp opened — just hit send"}
              </h3>
              <p style={{ fontSize: "var(--fs-base)", lineHeight: 1.55, color: colors.muted, maxWidth: "42ch", margin: "0 auto 22px" }}>
                {status === "sent" ? (
                  <>
                    Thanks — your enquiry is with us and we'll reply within one working day.
                    For anything urgent, WhatsApp us on{" "}
                    <PhoneLink style={{ color: colors.brown, fontWeight: 600 }} />.
                  </>
                ) : (
                  "Your enquiry is prefilled in WhatsApp with your details. If it didn't open, check that pop-ups are allowed, or call us directly."
                )}
              </p>
              <button
                type="button"
                onClick={onReset}
                style={{
                  // The only route back to the form once it has been replaced,
                  // and it was a 19px-tall text run. Padding takes it to 44px
                  // without changing where the underline sits.
                  minHeight: "44px",
                  padding: "10px 14px",
                  fontSize: "var(--fs-base)",
                  color: colors.brown,
                  cursor: "pointer",
                  border: "none",
                  borderBottom: "1px solid #C9B896",
                  background: "transparent",
                }}
              >
                Send another enquiry
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
